import { TranslationsMap } from '../i18n/translate';
import { AppDefId } from '../../constants/constants';
import {
  ISettings,
  Theme,
  PrivacyPolicyType,
  Audience,
  ButtonPositionType,
} from '../../types';
import { getInitialSettings } from '../configuration/get-initial-settings';
import {
  PrivacyPolicyType as ApiPrivacyPolicyType,
  Theme as ApiTheme,
  Audience as ApiAudience,
  GetCookieBannerSettingsResponse,
  ButtonPosition,
} from '@wix/ambassador-serverless-cookie-consent-settings-serverless/types';

export interface ComponentSettings {
  settings: ISettings;
  translations: TranslationsMap;
}
class ComponentSettingsService {
  settings?: ComponentSettings;

  async getSettingsAndTranslations() {
    const languageCode = window.wixEmbedsAPI?.getLanguage() || 'en';

    this.settings = await fetch(
      `/_serverless/cookie-consent-settings-serverless/v1/cookie-banner-settings?languageCode=${languageCode}`,
      {
        method: 'GET',
        headers: {
          authorization: getAuthorization(),
          'x-wix-client-artifact-id': 'cookie-consent-banner-for-uou',
        },
      },
    )
      .then((res) => res.json())
      .then(mapSettingsApiResponse)
      .catch((reason) => {
        console.log(
          `error receiving component settings from serverless ${reason}`,
        );
        return this.loadSettings().then(() => this.settings);
      });

    return {
      settings: this.settings?.settings!,
      translations: {
        ...this.settings?.settings?.texts,
        ...this.settings?.translations,
      },
    };
  }

  async getSettings(): Promise<ISettings> {
    await this.loadSettings();
    return this.settings?.settings || getInitialSettings();
  }

  async getTranslations(): Promise<TranslationsMap> {
    await this.loadSettings();
    return {
      ...this.settings?.settings?.texts,
      ...this.settings?.translations,
    };
  }

  async loadSettings() {
    const locale = window.wixEmbedsAPI?.getLanguage() || 'en';
    this.settings = await fetch(
      `/_api/app-settings-service/v1/settings/components/${AppDefId}?languageKey.languageCode=${locale}&host=BUSINESS_MANAGER&state=NR`,
      {
        method: 'GET',
        cache: 'no-cache',
        headers: {
          Authorization: getAuthorization(),
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'x-wix-client-artifact-id': 'cookie-consent-banner-for-uou',
        },
      },
    )
      .then((res) => res.json())
      .catch((reason) => {
        console.log(`error receiving component settings ${reason}`);
        return getInitialSettings();
      });
  }
}

export const componentSettingsService = new ComponentSettingsService();

const getAuthorization = (): string =>
  window.wixEmbedsAPI?.getAppToken(AppDefId) || '';

const mapButtonPosition = (position?: ButtonPosition) => {
  switch (position) {
    case ButtonPosition.bottom_left:
      return ButtonPositionType.BottomLeft;
    case ButtonPosition.bottom_right:
      return ButtonPositionType.BottomRight;
    case ButtonPosition.center_left:
      return ButtonPositionType.CenterLeft;
    case ButtonPosition.center_right:
      return ButtonPositionType.CenterRight;
    default:
      return ButtonPositionType.BottomLeft;
  }
};

const mapSettingsApiResponse: (
  response: GetCookieBannerSettingsResponse,
) => ComponentSettings = ({ settings, translations }) => {
  return {
    settings: {
      ...settings,
      texts: settings?.texts || {},
      appEnabled: settings?.appEnabled || false,
      expiryDate: new Date(settings?.expiryDate || 0).getTime(),
      theme: settings?.theme === ApiTheme.dark ? Theme.dark : Theme.light,
      privacyPolicyType:
        settings?.privacyPolicyType === ApiPrivacyPolicyType.external_url
          ? PrivacyPolicyType.ExternalUrl
          : PrivacyPolicyType.PageOnSite,
      privacyPolicyPage: settings?.privacyPolicyPage || '',
      privacyPolicyUrl: settings?.texts?.privacyPolicyUrl || '',
      audience:
        settings?.audience === ApiAudience.all_visitors
          ? Audience.AllVisitors
          : Audience.EUVisitors,
      declineAllConfig: {
        enabled: settings?.declineAllConfig?.enabled || false,
        geo: settings?.declineAllConfig?.geo || [],
      },
      revisitSettingsConfig: {
        enabled: settings?.revisitSettingsConfig?.enabled || false,
        buttonPosition: mapButtonPosition(
          settings?.revisitSettingsConfig?.buttonPosition,
        ),
      },
    },
    translations: translations || {},
  };
};
